<template>
  <div class="container">
    <div class="row">
      <div class="col selector">
        <v-flex>
          <v-text-field
            id="scanner"
            v-model="barcode"
            box
            :label="$t('pallet_scanner.barcode')"
            autofocus
            @keyup.enter="performScan"
          ></v-text-field>
        </v-flex>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <div class="console">
          <div id="console" class="panel_scrollable">
            <p v-for="(log, index) in logs" :key="index">
              <span :style="log.style">{{ log.text }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="pallet_pictures" v-if="showPallet">
      <pallet-top :pallet="pallet" :divider="divider" :settings="settings" :showTexts="true" />
      <pallet-side :pallet="pallet" :divider="divider * 1.5" :settings="settings" />
    </div>
  </div>
</template>

<script>
import PalletTop from "../pallets/PalletTop.vue";
import PalletSide from "../pallets/PalletSide.vue";
import Pallet from "@/models/Pallet.js";
import Product from "@/models/Product.js";
import User from "@/models/User.js";
import Order from "@/models/Order.js";
export default {
  components: {
    PalletTop,
    PalletSide
  },
  data() {
    return {
      logs: [],
      pallet: {},
      orderProducts: [],
      loaded: [],
      barcode: "",
      showPallet: false,
      searchingForPallet: true,
      settings: {
        palletHeight: 120,
        palletWeight: 2000,
        spacingBetween: 0,
        spacingEnds: 0,
        spacingSides: 0
      },
      defaultSettings: {
        palletHeight: 120,
        palletWeight: 2000,
        spacingBetween: 0,
        spacingEnds: 0,
        spacingSides: 0
      },
      divider: 6,
      user: new User(this.$session.get("user"))
    };
  },
  methods: {
    /**
     * To scan pallet perform these actions
     * 1. If I currently have this product, just make it green and print log,
     * 2. If I don't have it just grab the first with same position, make it green and print log (also update my pallet data, swap with the one overwritten)
     * 3. If empty, try to find pallet with this barcode (recursion might work if pallet is set to {} before)
     * 4. If still not found, log
     */
    async performScan() {
      if (!this.user.hasRights("PAL_SCAN")) {
        this.barcode = "";
        alert(this.$t("pallet_scanner.no_right_to_scan"));
        return 0;
      }
      //check if we have already loaded a pallet and order (looking for pallet here)
      if (
        (Object.entries(this.pallet).length === 0 &&
          this.pallet.constructor === Object) ||
        this.searchingForPallet
      ) {
        this.searchingForPallet = false;
        //load pallet to work with, try to load everytime
        const palletResponse = await Pallet.updatePalletByBarcode(this.barcode);
        if (palletResponse.status == 200) {
          this.pallet = new Pallet(palletResponse.data);
          //fetch all order products to know if the next scan will be successful
          const orders = await Order.loadOrders({
            orders: this.pallet.getOrderIdsString()
          });
          let orderProducts = [];
          orders.forEach(order => {
            order.products.forEach(product => {
              orderProducts.push(new Product(product));
            });
          });
          this.orderProducts = orderProducts;
          this.showPallet = true;
          const logText = `${this.$t(
            "pallet_scanner.pallet_loaded"
          )} ${this.barcode.slice(
            this.barcode.length - 4,
            this.barcode.length
          )}`;
          this.logs.unshift({
            style: { color: "blue" },
            text: logText
          });
          this.barcode = "";
          //just log for easier testing (delete this)
          this.pallet.products.sort((a, b) => {
            return a.y - b.y;
          });
          this.pallet.products.forEach(product => {
            console.log(product.barcode);
          });
          console.log("///////////////");
        } else if (palletResponse.status == 202) {
          const logText =
            this.$t("pallet_scanner.pallet") +
            " " +
            this.barcode +
            " " +
            this.$t("pallet_scanner.not_ready");
          this.logs.unshift({
            style: { color: "rgb(255, 127, 0)" },
            text: logText
          });
          this.barcode = "";
        } else {
          const logText =
            this.$t("pallet_scanner.not_found") + " " + this.barcode;
          this.logs.unshift({
            style: { color: "red" },
            text: logText
          });
          this.barcode = "";
        }
        return 0;
      }
      //------looking for product from here--------
      Product.scan(this.pallet.id, this.barcode); //perform scan before everything
      //try to find within order
      const withinOrder = this.orderProducts.find(product => {
        return product.barcode == this.barcode;
      });
      if (withinOrder && withinOrder.part_of != null) {
        const logText =
          this.$t("pallet_scanner.not_found") + " " + this.barcode;
        this.logs.unshift({
          style: { color: "red" },
          text: logText
        });
        this.barcode = "";
        return 5;
      }
      if (withinOrder) {
        if (this.loaded.includes(withinOrder.id)) {
          if (withinOrder.status >= 13 || withinOrder.status < 15) {
            const errorText = `${this.$t("pallet_scanner.product")} ${
              this.barcode
            } ${this.$t("pallet_scanner.product_already_loaded")}`;
            this.logs.unshift({
              style: { color: "red" },
              text: errorText
            });
            this.barcode = "";
            return 0;
          }
        }
        //first, try to find in the pallet
        const found = this.pallet.products.find(product => {
          return product.barcode == this.barcode;
        });
        if (found) {
          //if there is a product within a pallet, just mark it as scanned
          let tempPallet = new Pallet(this.pallet);
          const productIndex = this.pallet.products.indexOf(found);
          tempPallet.products[productIndex].status = 13;
          this.pallet = tempPallet;
          this.loaded.push(found.id);
        } else {
          //if not found within the pallet but it is still in the order, try to find with same parameters within the order
          const sameWithinPallet = this.pallet.products.find(product => {
            return (
              product.position == withinOrder.position &&
              withinOrder.status < 13 &&
              product.status < 13 &&
              !this.loaded.includes(product.id)
            );
          });
          const productIndex = this.pallet.products.indexOf(sameWithinPallet);
          if (productIndex > -1) {
            let tempPallet = new Pallet(this.pallet);
            tempPallet.products[productIndex] = withinOrder;
            tempPallet.products[productIndex].x = sameWithinPallet.x;
            tempPallet.products[productIndex].y = sameWithinPallet.y;
            tempPallet.products[productIndex].z = sameWithinPallet.z;
            tempPallet.products[productIndex].is_flipped =
              sameWithinPallet.is_flipped;
            tempPallet.products[productIndex].status = 13;
            this.pallet = tempPallet;
            this.loaded.push(withinOrder.id);
          } else {
            const logText =
              this.$t("pallet_scanner.product_with_barcode") +
              this.barcode +
              this.$t("pallet_scanner.should_be_on_pallet") +
              withinOrder.pallet.toString();
            this.logs.unshift({
              style: { color: "red" },
              text: logText
            });
            this.barcode = "";
            return 2;
          }
        }

        const logText = `${this.$t("pallet_scanner.product")} ${
          this.barcode
        } ${this.$t("pallet_scanner.loaded")} ${this.pallet.toString()}`;
        this.logs.unshift({
          style: { color: "green" },
          text: logText
        });
        this.barcode = "";
      }
      if (!withinOrder) {
        this.searchingForPallet = true;
        this.performScan();
      }
    }
  }
};
</script>

<style scoped>
.console {
  position: relative;
  width: 100%;
  height: 7em;
  overflow: hidden;
  background-color: rgba(87, 88, 86, 0.11);
}
.panel_scrollable {
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  padding-right: 10%;
  box-sizing: content-box;
}
</style>
